<script setup>
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import '@splidejs/vue-splide/css';
const props = defineProps({
  relatedPosts: {
    type: Object,
    required: true
  }
})

const postsCards = props.relatedPosts
const cards = []
const windowWidth = ref(0)
let enoughSlides

const PLACEHOLDER = 'https://via.placeholder.com/300x150'
const { altFallback } = usePageAltFallback()
const relatedImageSrc = (card) =>
  card?.image?.data?.attributes?.url || card?.boltImage || PLACEHOLDER
const relatedImageAlt = (card) =>
  resolveMediaAlt(card?.image?.data?.attributes || card?.image, card?.title, altFallback.value)

for (let i = 0; i < postsCards.length; i++) {
  cards.push(postsCards[i].attributes)
}
// Carousel options
const options = {
  perPage: 1,
  perMove: 1,
  pagination: false,
  type: 'slide',
  speed: 500,
  autoWidth: true,
  arrows: true,
  gap: '20px',
  mediaQuery: 'min',
  breakpoints: { // These are mobile first
    768: {
      perPage: 2,
      perMove: 2
    },
    1024: {
      perPage: 3,
      perMove: 3
    }
  }
}

// Detection of elements needed for displaying the carousel or the slides without carousel
function enoughItems(windowWidth) {
  enoughSlides = false
  if (windowWidth >= 1024 && windowWidth < 1200 && cards.length > 3) {
    enoughSlides = true
  } else if (windowWidth >= 768 && windowWidth < 1024 && cards.length > 2) {
    enoughSlides = true
  } else if (windowWidth < 768 && cards.length > 1) {
    enoughSlides = true
  }
  return enoughSlides
}

// Window resizing event listener
onMounted(() => {
  windowWidth.value = window.innerWidth
  window.addEventListener('resize', function () {
    windowWidth.value = window.innerWidth
  })
})
</script>

<template>
  <h2 class="related-title-section">Related posts</h2>
  <!-- Gallery in case the screen is smaller than expected -->
  <section class="related-posts" v-if="enoughItems(windowWidth)" :val="enoughItems(windowWidth)">
    <Splide :options="options">
      <SplideSlide v-for="(card, index) in cards" :key="index">
        <a :href="`/blog/${card.slug}`" class="related-post">
          <div class="related-image">
            <img
              :src="relatedImageSrc(card)"
              :alt="relatedImageAlt(card)"
              loading="lazy"
              decoding="async"
            >
          </div>
          <div class="related-tags">
            <div
              class="tag"
              v-for="cat in card.categories.data"
              :key="cat.id || cat.attributes?.category"
              v-html="cat.attributes.category"
            ></div>
          </div>
          <div class="related-title" v-html="card.title"></div>
          <div class="related-description" v-html="card.shortDescription"></div>
        </a>
      </SplideSlide>
    </Splide>
  </section>
  <!-- Else just display the cards -->
  <section v-else class="row align-items-stretch justify-content-center related-posts">
    <div
      class="col-3"
      v-for="post in relatedPosts"
      :key="post.id || post.attributes?.slug"
    >
      <a :href="`/blog/${post.attributes.slug}`" class="related-post">
        <div class="related-image">
          <img
            :src="relatedImageSrc(post.attributes)"
            :alt="relatedImageAlt(post.attributes)"
            loading="lazy"
            decoding="async"
          >
        </div>
        <div class="related-tags">
          <div
            class="tag"
            v-for="cat in post.attributes.categories.data"
            :key="cat.id || cat.attributes?.category"
            v-html="cat.attributes.category"
          ></div>
        </div>
        <div class="related-title" v-html="post.attributes.title"></div>
        <div class="related-description" v-html="post.attributes.shortDescription"></div>
      </a>
    </div>
  </section>
</template>

<style lang="scss">
  .related-title-section{
    margin-bottom: 8px;
  }
  .related-posts{
    // Gallery styles
    .splide{
      .related-post{
        max-width: 270px;
      }
    }

    // No gallery styles
    .related-post{
      text-decoration: none;
      color: $color-body-text;
      display: block;
      border: solid 1px $color-light-gray;
      padding: 10px;
      border-radius: 24px;
      height: 100%;

      &:hover{
        text-decoration: none;
        font-weight: 400;
        .related-tags{
          font-weight: 400;
        }
        .related-description{
          font-weight: 400;
        }
      }

      .related-image{
        margin-bottom: 10px;
        height: 155px;
        overflow: hidden;
        border-radius: 12px;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
      }
      .related-tags{
        margin-bottom: 10px;
        .tag{
          color: $color-body-text;
          display: inline-block;
          border-radius: 10px;
          padding: 2px 8px;
          background-color: $color-accent;
          font-size: $font-size-smaller;
        }
      }
      .related-title{
        color: $color-body-text;
        font-weight: 600;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .related-description{
        color: $color-subtext;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        p:last-child{
          margin: 0;
        }
      }
    }
  }
</style>